import React, {ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {createPortal, flushSync} from 'react-dom';
import {Button, Icon, MenuItem} from '@mc/ui-v4';
import {mcPrint} from '@mc/react-ui-icons';
import {useAppConfig} from '@mc/react-app-base';
import './FeatureDetailPrint.scss';
import {FeatureDetailVO, FeatureImageVO, LayerVO} from '../../api';
import {getFeatureImageUrl} from '../../api/urls';
import {sanitizeFeatureDetailHtml} from '../../helpers/sanitizeHtml';
import {renderAccessibilityIcons} from '../../assets/accessibility/accessibilityCodelist';
import {layerColorToCss} from '../search-panel/searchPanelTypes';

const PRINT_LABEL = 'Tisk';

/** Pojistka pro prohlížeče, které nepošlou událost afterprint (jinak by tiskový obsah zůstal v DOM). */
const AFTER_PRINT_FALLBACK_MS = 60000;

// ---------------------------------------------------------------------------------------------
// Co se tiskne
// ---------------------------------------------------------------------------------------------

export type FeatureDetailPrintProps = {
    /** Název objektu – v tisku slouží jako nadpis (pokud detail nemá vlastní hlavičku) a jako titulek stránky. */
    title?: string;
    /** HTML hlavičky detailu ze serverové šablony (název, adresa, kontakty, kategorie…). */
    headerHtml?: string;
    /** HTML těla detailu ze serverové šablony. */
    bodyHtml?: string;
    /** Obsah detailu vykreslený na klientu – použije se, pokud detail nepřichází ze serverové šablony. */
    children?: ReactNode;
    /** URL fotografií v plné velikosti – v tisku nechceme miniatury. */
    imageUrls?: string[];
    /** Barva vrstvy pro hlavičku ze šablony (CSS proměnná --praha1-layer-color). */
    layerColor?: string;
};

/**
 * Tisková sestava detailu objektu (MCW-29120). Obsah je shodný s detailem v aplikaci, pouze fotografie
 * jsou v plné velikosti místo miniatur.
 *
 * Sestavu renderujeme portálem přímo do <body>, aby ji `@media print` (FeatureDetailPrint.scss) mohlo
 * nechat jako jediný viditelný prvek stránky – díky tomu se použijí stejné styly aplikace jako
 * v detailu a nemusíme skládat samostatný dokument v novém okně. V DOM je jen po dobu tisku a na
 * obrazovce je odsunutá mimo viditelnou oblast, proto je skrytá i pro odečítače obrazovky.
 */
const FeatureDetailPrint: React.FC<FeatureDetailPrintProps> = ({title, headerHtml, bodyHtml, children, imageUrls, layerColor}) => {
    const header: ReactNode = headerHtml ? (
        // Hlavičku i tělo sanitizujeme stejně jako v detailu (viz FeatureDetailSidebar) – čistí se jen
        // uzly s uživatelským formátovaným textem, ikony přístupnosti doplní renderAccessibilityIcons.
        <div
            className={'praha1-header'}
            style={layerColor ? ({'--praha1-layer-color': layerColor} as React.CSSProperties) : undefined}
            dangerouslySetInnerHTML={{__html: sanitizeFeatureDetailHtml(renderAccessibilityIcons(headerHtml))}}
        />
    ) : (
        title && <div className={'feature-detail-print-title'}>{title}</div>
    );

    const images: ReactNode = imageUrls?.length ? (
        <div className={'feature-detail-print-images'}>
            {imageUrls.map((url: string) => (
                <img key={url} src={url} alt={''} />
            ))}
        </div>
    ) : null;

    const body: ReactNode = (
        <>
            {bodyHtml && <div dangerouslySetInnerHTML={{__html: sanitizeFeatureDetailHtml(renderAccessibilityIcons(bodyHtml))}} />}
            {children}
        </>
    );

    return createPortal(
        <div className={'feature-detail-print'} aria-hidden={true}>
            {header}
            {/* Pořadí shodné s detailem v aplikaci: u detailu se serverovou hlavičkou jsou fotografie nad textem, jinak pod ním. */}
            {headerHtml ? (
                <>
                    <div className="mt-1 mb-1">{images}</div>
                    {body}
                </>
            ) : (
                <>
                    {body}
                    <div className="mt-1 mb-1">{images}</div>
                </>
            )}
        </div>,
        document.body,
    );
};

// ---------------------------------------------------------------------------------------------
// Přednačtení obrázků – tisk pak nemusí na nic čekat
// ---------------------------------------------------------------------------------------------

/** Držíme reference na přednačtené obrázky, ať je prohlížeč nezahodí a nestahuje je znovu. */
const preloadedImages = new Map<string, HTMLImageElement>();

/** Adresy obrázků v HTML detailu (fotografie ze serverové šablony, ikony přístupnosti…). */
const collectHtmlImageUrls = (html?: string): string[] => {
    if (!html) {
        return [];
    }
    // Ikony přístupnosti vznikají až v renderAccessibilityIcons, proto stejná transformace jako v tisku.
    const doc = new DOMParser().parseFromString(renderAccessibilityIcons(html), 'text/html');
    return Array.from(doc.querySelectorAll('img')).map((img: HTMLImageElement): string => img.getAttribute('src'));
};

/** Načte do cache prohlížeče všechny obrázky, které se v tisku detailu objeví. */
const preloadPrintImages = (data: FeatureDetailPrintProps): void => {
    [...(data.imageUrls ?? []), ...collectHtmlImageUrls(data.headerHtml), ...collectHtmlImageUrls(data.bodyHtml)]
        .filter((url: string): boolean => Boolean(url) && !preloadedImages.has(url))
        .forEach((url: string): void => {
            const image = new Image();
            image.src = url;
            preloadedImages.set(url, image);
        });
};

// ---------------------------------------------------------------------------------------------
// Jak se tiskne
// ---------------------------------------------------------------------------------------------

export type FeatureDetailPrintApi = {
    /** Spustí tisk detailu. Volat přímo z obsluhy uživatelské akce, ať tisk nevypadá jako samočinný. */
    print: (data: FeatureDetailPrintProps) => void;
    /** Tisková sestava – vlož do stromu komponenty, která hook používá. */
    node: ReactNode;
};

/**
 * Vytiskne stránku pod názvem objektu (prohlížeč ho vytiskne v záhlaví a nabídne jako název PDF)
 * a po dokončení tisku uklidí. window.print() je v prohlížečích na počítači blokující, na mobilech
 * ale ne – úklid proto věšíme na afterprint.
 */
const printPage = (title: string | undefined, onFinish: () => void): void => {
    const previousTitle = document.title;
    let fallbackTimer: number;
    let finished = false;
    const finish = (): void => {
        if (finished) {
            return;
        }
        finished = true;
        window.clearTimeout(fallbackTimer);
        document.title = previousTitle;
        onFinish();
    };

    if (title) {
        document.title = title;
    }
    window.addEventListener('afterprint', finish, {once: true});
    fallbackTimer = window.setTimeout(finish, AFTER_PRINT_FALLBACK_MS);
    window.print();
};

/**
 * Řízení tisku detailu objektu (MCW-29120).
 *
 * Tiskovou sestavu namontujeme synchronně (flushSync) a hned tiskneme – vše ještě v obsluze kliknutí
 * uživatele, takže tisk nečeká na načtení obrázků (ty se přednačítají už při zobrazení tiskové akce)
 * a tiskový dialog se otevře okamžitě.
 *
 * Pozor na Safari: to se od druhého tisku na téže stránce ptá „Tato webová stránka se pokouší
 * vytisknout sama sebe." Nejde to obejít – ověřeno testovací stránkou se čtyřmi variantami tisku
 * (window.print() bez změny stránky, s vložením a odebráním obsahu, z nově vytvořeného iframu
 * i ze sdíleného iframu); Safari se ptá u všech. V ostatních prohlížečích se dialog otevře rovnou.
 */
export const useFeatureDetailPrint = (): FeatureDetailPrintApi => {
    const [printData, setPrintData] = useState<FeatureDetailPrintProps | null>(null);

    const print = useCallback((data: FeatureDetailPrintProps): void => {
        flushSync((): void => setPrintData(data));
        // Po tisku sestavu odmontujeme, ať běžný tisk stránky (Ctrl+P) netiskne poslední detail.
        printPage(data.title, (): void => setPrintData(null));
    }, []);

    return {print, node: printData && <FeatureDetailPrint {...printData} />};
};

// ---------------------------------------------------------------------------------------------
// Odkud se tisk spouští
// ---------------------------------------------------------------------------------------------

type PrintActionProps = {
    /** Řízení tisku z `useFeatureDetailPrint` v nadřazené komponentě. */
    api: FeatureDetailPrintApi;
    /** Detail objektu ze serverové šablony – obsah tisku je pak shodný s detailem v panelu. */
    item?: FeatureDetailVO;
    /** Vrstva objektu (barva hlavičky v tisku). */
    layersConfig?: LayerVO;
    /** Název objektu – nadpis tisku a název nabídnutého PDF. */
    title?: string;
    /** Obsah tisku poskládaný na klientovi – použije se, když detail nepřichází ze serverové šablony. */
    children?: ReactNode;
};

/**
 * Podklad pro tisk. Obrázky přednačteme hned, jak se tisková akce zobrazí, aby tisk nemusel na nic
 * čekat a mohl proběhnout celý v obsluze kliknutí uživatele.
 */
const usePrintData = ({item, layersConfig, title, children}: PrintActionProps): FeatureDetailPrintProps => {
    //@ts-ignore
    const {serverContextPath} = useAppConfig();
    const data: FeatureDetailPrintProps = useMemo(
        (): FeatureDetailPrintProps => ({
            title,
            children,
            headerHtml: item?.headerHtml,
            bodyHtml: item?.html,
            // V tisku chceme fotografie v plné velikosti, ne miniatury jako v detailu.
            imageUrls: item?.images?.map((image: FeatureImageVO): string => getFeatureImageUrl(item, image.id, serverContextPath)),
            layerColor: layerColorToCss(layersConfig?.style?.color ?? layersConfig?.iconColor),
        }),
        [item, layersConfig, title, children, serverContextPath],
    );
    useEffect((): void => preloadPrintImages(data), [data]);
    return data;
};

/** Tlačítko tisku detailu objektu (MCW-29120). */
export const FeatureDetailPrintButton: React.FC<PrintActionProps & {className?: string; iconSize?: 'lg'; style?: 'link'}> = props => {
    const data: FeatureDetailPrintProps = usePrintData(props);
    return (
        <Button
            aria-label={PRINT_LABEL}
            className={props.className}
            icon={<Icon icon={mcPrint} size={props.iconSize} />}
            onClick={(): void => props.api.print(data)}
            style={props.style}
        />
    );
};

/** Položka menu „Tisk" – chová se stejně jako tlačítko, jen v kebab menu detailu. */
export const FeatureDetailPrintMenuItem: React.FC<PrintActionProps> = props => {
    const data: FeatureDetailPrintProps = usePrintData(props);
    return <MenuItem icon={<Icon icon={mcPrint} />} onClick={(): void => props.api.print(data)} text={PRINT_LABEL} />;
};
